import { LoaderFunction } from 'react-router-dom';

import {
  getMyLearning,
  getRecentlyViewedLessons,
  MyLearningItem,
  MyLearningData,
  RecentlyViewedLessonItem,
  getArchivedContent,
} from 'src/services/http/myLearning';
import { fetchGraphQL } from 'src/services/contentful/client';
import { GetAllMyLearningEntriesQuery, GetAllMyLearningEntriesQueryVariables } from 'src/graphql/generated/graphql';
import {
  CollectedDataItem,
  CourseEntry,
  GET_ALL_MY_LEARNING_ENTRIES,
  LessonEntry,
  MatchingDataItem,
} from 'src/graphql/queries/GetAllMyLearningEntries';
import { MyLearningPageContentSections } from 'src/pages/MyLearningPage/constants';
import { getContentProduct } from 'src/utils/getContentProduct';
import { getSlug } from 'src/utils/slug';
import { NotificationItem } from 'src/services/http/notifications';
import { LearningProgressStatus } from 'src/services/http/progress';
import { FlattenedLearningInstance } from 'src/utils/almDataTransformer';

import { allTrainingSessionsFlattened } from './__fixtures__/liveTrainingDataMock';

// Legacy loader types (kept separate from types/index.ts due to differences)
export interface MyLearningLoaderProps {
  myLearningResourceData: MyLearningResourceData | null;
  recentlyViewedLessonsResources: RecentlyViewedLessonResource[] | null;
  archivedContentResources: ArchivedContentResource[] | null;
  areUnseenNotificationsAvailable?: boolean;
  liveTrainingData: FlattenedLearningInstance[] | null;
}

export interface MyLearningResourceData {
  learningPaths: MyLearningResource[] | null;
  courses: MyLearningResource[] | null;
}

export interface MyLearningResource {
  completedDate?: string;
  startedDate?: string;
  contentfulId: string;
  progressTotal: number;
  subCollectionTotal: number;
  slug?: string;
  title: string;
  badgeUrl?: string;
  learningPathSlug?: string;
  learningPathContentfulId: string | null;
  product?: string;
  products?: string;
  historicalNotifications: NotificationItem[];
  unseenNotifications: NotificationItem[];
  status?: LearningProgressStatus;
}

export interface RecentlyViewedLessonResource {
  contentfulId: string;
  lastVisitedDate: Date;
  progressTotal: number;
  subCollectionTotal: number;
  slug: string;
  title: string;
  courseSlug?: string;
  learningPathSlug?: string;
}

export interface ArchivedContentResource {
  entryTitle: string;
  completionDate: Date;
}

export const loader: LoaderFunction = async ({ params }): Promise<MyLearningLoaderProps> => {
  const myLearningPageStatus = params.status ?? '';

  const recentlyViewedLessonsData =
    myLearningPageStatus === MyLearningPageContentSections.IN_PROGRESS ? await getRecentlyViewedLessons() : null;

  const archivedContentData =
    myLearningPageStatus === MyLearningPageContentSections.COMPLETE ? await getArchivedContent() : null;

  const myLearningData = await getMyLearning(myLearningPageStatus);
  const sortedMyLearningData =
    myLearningPageStatus === MyLearningPageContentSections.COMPLETE
      ? sortCompletedData(myLearningData)
      : myLearningData;
  const mappedData = await getCoursesAndLearningPathsTitleAndSlugs(
    recentlyViewedLessonsData,
    sortedMyLearningData,
    archivedContentData,
  );

  const completeTabLearningData =
    myLearningPageStatus !== MyLearningPageContentSections.COMPLETE
      ? await getMyLearning(MyLearningPageContentSections.COMPLETE)
      : myLearningData;
  const newPathNotificationsAvailable = completeTabLearningData?.learningPath.filter(
    (path) => !!path.unseenNotifications.length,
  );
  const newCourseNotificationsAvailable = completeTabLearningData?.courses.filter(
    (course) => !!course.unseenNotifications.length,
  );

  let transformedLiveTrainingData: FlattenedLearningInstance[] | null = null;

  try {
    // TODO: Replace with real API call when ready
    // const liveTrainingData = await getLiveTraining(0, 10);
    // const resolvedLiveTrainingData = resolveAlmRelationshipsByIdPattern(liveTrainingData as any);
    // transformedLiveTrainingData = transformToEnrolledLearningInstances(resolvedLiveTrainingData);

    // Using mock data temporarily
    transformedLiveTrainingData = allTrainingSessionsFlattened;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting live training data:', error);
  }

  return {
    ...mappedData,
    areUnseenNotificationsAvailable:
      !!newPathNotificationsAvailable?.length || !!newCourseNotificationsAvailable?.length,
    liveTrainingData: transformedLiveTrainingData,
  };
};

//sort data by new notifications
export const sortCompletedData = (data: MyLearningData | null): MyLearningData => {
  const sortedPathData = data
    ? data.learningPath
        .sort((firstPath, secondPath) => firstPath.unseenNotifications.length - secondPath.unseenNotifications.length)
        .reverse()
    : [];

  const sortedCourseData = data
    ? data.courses
        .sort(
          (firstCourse, secondCourse) =>
            firstCourse.unseenNotifications.length - secondCourse.unseenNotifications.length,
        )
        .reverse()
    : [];

  return {
    learningPath: sortedPathData,
    courses: sortedCourseData,
  };
};

export const getCoursesAndLearningPathsTitleAndSlugs = async (
  recentlyViewedLessonsData: RecentlyViewedLessonItem[] | null,
  myLearningData: MyLearningData | null,
  archivedContentData: ArchivedContentResource[] | null,
): Promise<MyLearningLoaderProps> => {
  if (!recentlyViewedLessonsData && !myLearningData && !archivedContentData) {
    return {
      recentlyViewedLessonsResources: null,
      myLearningResourceData: null,
      archivedContentResources: null,
      liveTrainingData: null,
    };
  }

  const contentfulIds = getAllContentfulIds(recentlyViewedLessonsData, myLearningData);

  const response = await fetchGraphQL<GetAllMyLearningEntriesQuery, GetAllMyLearningEntriesQueryVariables>(
    GET_ALL_MY_LEARNING_ENTRIES,
    { contentfulIds },
  );

  if (
    !response.smCourseCollection.items.length &&
    !response.learningPathCollection.items.length &&
    !response.smLessonCollection.items.length
  ) {
    return {
      recentlyViewedLessonsResources: null,
      myLearningResourceData: null,
      archivedContentResources: archivedContentData,
      liveTrainingData: null,
    };
  }

  const collectedData: CollectedDataItem[] = [
    ...response.smCourseCollection.items,
    ...response.learningPathCollection.items,
    ...response.smLessonCollection.items,
  ];

  return {
    recentlyViewedLessonsResources: transformRecentlyViewedLessonsData(recentlyViewedLessonsData, collectedData),
    myLearningResourceData: transformMyLearningData(myLearningData, collectedData),
    archivedContentResources: archivedContentData,
    liveTrainingData: null,
  };
};

const getAllContentfulIds = (
  recentlyViewedLessonsData: RecentlyViewedLessonItem[] | null,
  myLearningData: MyLearningData | null,
): string[] => {
  const contentfulIds =
    recentlyViewedLessonsData
      ?.filter(
        (item: RecentlyViewedLessonItem) => !item.subCollectionTotal || item.progressTotal < item.subCollectionTotal,
      )
      .map((item) => item.contentfulId) ?? [];

  const courseContentfulIds =
    recentlyViewedLessonsData
      ?.filter((item) => item.courseContentfulId)
      .map((item) => item.courseContentfulId as string) ?? [];

  const learningPathContentfulIds =
    recentlyViewedLessonsData
      ?.filter((item) => item.learningPathContentfulId)
      .map((item) => item.learningPathContentfulId as string) ?? [];

  const myLearningContentfulIds =
    (myLearningData && [
      ...myLearningData.courses.map((item) => item.contentfulId),
      ...myLearningData.learningPath.map((item) => item.contentfulId),
    ]) ??
    [];

  return [...contentfulIds, ...courseContentfulIds, ...learningPathContentfulIds, ...myLearningContentfulIds];
};

const transformRecentlyViewedLessonsData = (
  recentlyViewedLessonsData: RecentlyViewedLessonItem[] | null,
  collectedData: CollectedDataItem[],
): RecentlyViewedLessonResource[] | null => {
  if (!recentlyViewedLessonsData) {
    return null;
  }

  return recentlyViewedLessonsData
    .map((item) => mapToRecentlyViewedLessonResource(item, collectedData))
    .filter((item): item is RecentlyViewedLessonResource => !!item);
};

const mapToRecentlyViewedLessonResource = (
  item: RecentlyViewedLessonItem,
  collectedItems: CollectedDataItem[],
): RecentlyViewedLessonResource | null => {
  const matchingItem = collectedItems.find((value) => value.sys.id === item.contentfulId);
  const matchingCourseItem = collectedItems.find((value) => value.sys.id === item.courseContentfulId);
  const matchingLearningPathItem = collectedItems.find((value) => value.sys.id === item.learningPathContentfulId);

  if (matchingItem) {
    return {
      ...item,
      title: matchingItem.title,
      slug: getSlug(matchingItem),
      lastVisitedDate: new Date(item.lastVisitedDate),
      subCollectionTotal: item.subCollectionTotal || (matchingItem as LessonEntry).sectionListCollection.total,
      ...(matchingCourseItem ? { courseSlug: getSlug(matchingCourseItem) } : {}),
      ...(matchingLearningPathItem ? { learningPathSlug: getSlug(matchingLearningPathItem) } : {}),
    };
  }

  return null;
};

export const transformMyLearningData = (
  myLearningData: MyLearningData | null,
  collectedItems: CollectedDataItem[],
): MyLearningResourceData => {
  const learningPaths =
    myLearningData?.learningPath
      .map((item) => mapToMyLearningResources(item, collectedItems))
      .filter((item): item is MyLearningResource => !!item) ?? [];

  const courses =
    myLearningData?.courses
      .map((item) => mapToMyLearningResources(item, collectedItems))
      .filter((item): item is MyLearningResource => !!item) ?? [];

  return {
    learningPaths: learningPaths.length ? learningPaths : null,
    courses: courses.length ? courses : null,
  };
};

const mapToMyLearningResources = (
  item: MyLearningItem,
  collectedItems: CollectedDataItem[],
): MyLearningResource | null => {
  const matchingItem = collectedItems.find((data) => data.sys.id === item.contentfulId) as MatchingDataItem | undefined;
  const matchingLearningPathItem = collectedItems.find((value) => value.sys.id === item.learningPathContentfulId);

  if (matchingItem) {
    return {
      ...item,
      product: getContentProduct(matchingItem.product),
      title: matchingItem.title,
      slug: getSlug(matchingItem),
      badgeUrl: (matchingItem as CourseEntry).reward?.badgeCdnUrl,
      ...(matchingLearningPathItem ? { learningPathSlug: getSlug(matchingLearningPathItem) } : {}),
    };
  }

  return null;
};
